import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, Lock } from 'lucide-react';

export function StatusPill({ status }: { status: 'stopped' | 'starting' | 'running' | 'failed' }) {
  const map = {
    stopped: 'bg-slate-100 text-slate-600 border-slate-200',
    starting: 'bg-amber-50 text-amber-700 border-amber-200',
    running: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
  } as const;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wide ${map[status]}`}>
      {status}
    </span>
  );
}

export function OpsPasswordGate({
  checkPassword,
  onSuccess,
}: {
  checkPassword: (password: string) => boolean;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-sm rounded-xl border border-slate-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ops-unlock-title"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Lock size={16} className="text-indigo-600" />
          <h2 id="ops-unlock-title" className="text-sm font-semibold text-slate-900">
            Ops Console Access
          </h2>
        </div>
        <form
          className="p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (checkPassword(password)) onSuccess();
            else setError('Incorrect password');
          }}
        >
          <p className="text-xs text-slate-500">
            Enter the local ops password to continue.
          </p>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Password</span>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-2 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100">
              <KeyRound size={14} className="text-slate-400 shrink-0" />
              <input
                ref={inputRef}
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                className="flex-1 text-sm outline-none bg-transparent text-slate-900"
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium py-2 transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}

export function PanelCard({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div>
          <h3 className="text-xs font-semibold text-slate-800">{title}</h3>
          {subtitle && <p className="text-[10px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}) {
  const styles = {
    primary: 'bg-indigo-600 hover:bg-indigo-500 text-white border-transparent',
    secondary: 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200',
    danger: 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200',
    ghost: 'bg-transparent hover:bg-slate-100 text-slate-600 border-transparent',
  } as const;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors disabled:opacity-50 ${styles[variant]}`}
    >
      {children}
    </button>
  );
}

export function TextInput({
  label,
  value,
  onChange,
  mono,
  secret,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  secret?: boolean;
}) {
  const [show, setShow] = useState(false);
  const inputType = secret ? (show ? 'text' : 'password') : 'text';

  return (
    <label className="block space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-slate-600">{label}</span>
        {secret && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="text-[10px] text-indigo-600 hover:underline"
          >
            {show ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 ${
          mono ? 'font-mono' : ''
        }`}
      />
    </label>
  );
}
